import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { getTvwall } from '../tvwall-assets.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad plein écran.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'tvwall',
  family: 'photographic',
  danceability: 0.88,
  energyLevel: 0.82,
  bpmMin: 90,
  bpmMax: 170,
  movement: 0.75,
  tonality: 0.45,
  enabled: true
};

// Tvwall : un mur de vieux téléviseurs cathodiques PHOTOGRAPHIÉ (tvwall-off /
// tvwall-on, découpé par tools/build-tvwall.mjs : 28 écrans, leurs masques
// aux coins arrondis, l'homographie et le bombé de chacun, les reflets du
// verre, la lumière que chaque écran jette sur les boîtiers). Chaque
// téléviseur montre sa chaîne ; sur les temps forts, tous se synchronisent
// pour former une seule image géante répartie sur le mur. Techno, électro,
// hip-hop, synthwave, rock. Règles R1-R20 de kiosk-app/CLAUDE.md, preset
// party de lib/vdjLook.js.
//
// LE RENDU, un quad plein écran, la photo en cover-fit (R18, échelle max,
// recadrage centré, toutes les couches dérivées sous la même transformation) :
//   couleur = off (les boîtiers)
//           + spill(pixel) x couleur moyenne du contenu de l'écran qui
//             éclaire ce pixel (le mur se colore selon les écrans)
//           + dans chaque écran : contenu lu aux coordonnées (u, v) CUITES
//             au build (homographie + barillet, texture uv.png : la
//             perspective réelle, les coins arrondis par le masque adouci)
//             x filtre CRT (balayage une ligne sur trois de l'écran réel,
//             masque RVB très léger, vignettage, aberration au bord,
//             phosphore gonflé sur les blancs), puis les reflets du verre en
//             mode screen par-dessus.
// Le contenu est lu NET (R1) : l'atlas des chaînes fait 1280 x 720 (tuiles
// de 320 x 240, un écran de la photo fait 100 à 200 px, 140 à 280 à
// l'affichage).
//
// LES CHAÎNES : deux cibles 1280 x 720 (tier 0 : 960 x 540) rendues par un
// même shader. La SOURCE GÉANTE est projetée dans le repère de l'ÉCRAN
// ENTIER : chaque téléviseur montre la partie de l'image qui est derrière
// lui, les boîtiers la masquent, le cerveau recompose - donc des formes
// GRANDES et CONTRASTÉES : la pochette agrandie et saturée (R18), le titre
// en capitales très grasses (R12), des anneaux pleins, un spectre. L'écran
// HÉROS (le plus grand, au centre) montre une mesure sur deux la source
// entière. L'atlas 4 x 3 porte les chaînes : barres de couleur, pochette en
// gros plan, oscilloscope, amorce de compte à rebours, damier, mire.
//
// LA SYNCHRONISATION : sur le temps 1 de chaque mesure, tous basculent en
// VAGUE (depuis le centre ou en diagonale, sur 1/4 de temps) sur la source
// géante pendant un temps. Chaque bascule passe par un éclair de neige de
// 1/8 à 1/4 de temps. Sur les kicks, un écran s'éteint (l'image se rétracte
// en une ligne puis un point blanc) et se rallume.
// LA NEIGE, dosée : fine (grains de 1 à 2 px de l'écran réel, jamais des
// blocs), seulement aux transitions et sur 2 à 4 écrans « en panne » au
// plus, jamais plus de 20 % des écrans.
// LE RYTHME (party, R9) : TEMPS 2 à 4 bascules de chaîne, décrochage
// vertical sur les temps 2 et 4, le spill colore le mur ; MESURE
// synchronisation générale ; PHRASE nouvelle source géante, palette
// suivante ; MONTÉE neige plus fréquente (sous le plafond) et
// synchronisation à chaque temps ; DROP tous s'éteignent, un temps de
// silence, rallumage simultané sur la pochette, flash (VdjComfort). Sous
// 0,6 d'intensité : une bascule par temps, pas de décrochage.
// SCÈNE (R20, 32 s) : synchronisation sur la pochette dans les 2 premières
// secondes. CONFORT (R10) : le même shader en 32 x 32 toutes les 0,5 s,
// gain par VdjComfort nourri de mesures fraîches.
//
// REPLIS : sans tvwall-on, le build devine les écrans sur la photo éteinte
// (moins précis) ; sans photo du tout, le mur PROCÉDURAL de la première
// version (grille de boîtiers dessinés, 4 x 3 à 8 x 6).
//
// Debug : ?tvscreens=1 (contours des masques, teinte par indice - les
// numéros sont sur tvwall.<hash>.check.png) ; ?tvsync=1 (toujours la source
// géante) ; ?tvsnow=1 (tous en neige) ; ?tvhero=1 (le héros montre toujours
// la source entière) ; ?tvgrid=6x4 (grille du repli procédural).

const ATLAS = [4, 3];
const MAXC = 8, MAXR = 6;           // la texture d'état : 48 écrans au plus
const SCENE_S = 32;
const PHOTO_A = 1376 / 768;
const TIER = {
  0: { srcW: 960, srcH: 540, mask: 0, grids: [[4, 3]] },
  1: { srcW: 1280, srcH: 720, mask: 1, grids: [[5, 4], [4, 3], [6, 4]] },
  2: { srcW: 1280, srcH: 720, mask: 1, grids: [[6, 4], [5, 4], [8, 6], [4, 3]] }
};
const MODE = { channel: 0, giant: 1, snow: 2, roll: 3 };
const GIANTS = 4;
const HUE_SHIFTS = [0, 0.5, 0.25, 0.75];
const SNOW_MAX_FRACTION = 0.2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash1(n) { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); }

export function titleCaps(title) {
  const s = (title || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  return s.length >= 2 ? s.slice(0, 12).trim() : '';
}
function titleTexture(text) {
  if (typeof document === 'undefined') return null;
  const W = 1024, H = 256;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#000'; g.fillRect(0, 0, W, H);
  g.fillStyle = '#fff';
  let size = 230;
  g.font = `900 ${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  while (size > 60 && g.measureText(text).width > W - 40) { size -= 10; g.font = `900 ${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`; }
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(text, W / 2, H / 2 + size * 0.04);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- le contenu : la source géante (uAtlas = 0) ou l'atlas des chaînes (1) ----
const CONTENT_FRAGMENT = `
  precision highp float;
  uniform sampler2D uCover;
  uniform sampler2D uTitle;
  uniform float uHasCover;
  uniform float uCoverAspect;
  uniform float uAtlas;
  uniform float uGiant;          // 0 pochette, 1 titre, 2 anneaux, 3 spectre
  uniform float uTime;
  uniform float uBeat;
  uniform vec3 uBands;
  uniform vec3 uPalA;
  uniform vec3 uPalB;
  uniform vec3 uPalC;
  uniform vec2 uRes;
  varying vec2 vUv;
  const float A = 16.0 / 9.0;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec3 coverFit(vec2 uv, float frameAspect, float zoom, vec2 pan) {
    vec2 p = (uv - 0.5) * vec2(frameAspect, 1.0) / zoom + pan;
    if (uHasCover < 0.5) {
      float d = length(p) * 2.0;
      return mix(uPalA, uPalB, smoothstep(0.1, 0.9, d)) * (0.6 + 0.4 * step(0.5, fract(d * 4.0 - uBeat * 0.5)));
    }
    float side = max(frameAspect, 1.0);
    vec2 cuv = p / side * vec2(1.0, uCoverAspect) + 0.5;
    return texture2D(uCover, cuv).rgb;
  }
  vec3 giant(vec2 uv) {
    if (uGiant < 0.5) {
      // la pochette agrandie et SATURÉE : elle doit se lire par morceaux
      vec3 c = coverFit(uv, A, 1.3 + 0.05 * sin(uBeat * 0.4), vec2(0.0));
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      return clamp(mix(vec3(l), c, 1.6) * 1.15, 0.0, 1.0);
    }
    if (uGiant < 1.5) {
      vec3 bg = mix(uPalA, uPalB, smoothstep(0.3, 0.7, uv.y)) * 0.3;
      float t = texture2D(uTitle, vec2(uv.x, (uv.y - 0.5) * 2.25 + 0.5)).r;
      return mix(bg, vec3(0.9, 0.9, 0.88), t);
    }
    if (uGiant < 2.5) {
      // des anneaux PLEINS et larges
      vec2 p = (uv - 0.5) * vec2(A, 1.0);
      float r = length(p);
      vec3 c = vec3(0.0);
      for (int i = 0; i < 3; i++) {
        float fi = float(i);
        float rr = 0.16 + 0.17 * fi + 0.03 * sin(uBeat * 1.3 + fi);
        float d = abs(r - rr) * uRes.y;
        vec3 col = i == 0 ? uPalA : (i == 1 ? uPalB : uPalC);
        c += col * (1.0 - smoothstep(18.0, 24.0, d)) * 0.95;
      }
      return c;
    }
    float k = floor(uv.x * 12.0), f = k / 11.0;
    float lvl = mix(uBands.x, uBands.y, smoothstep(0.0, 0.5, f)) * (1.0 - f * 0.4) + mix(0.0, uBands.z, smoothstep(0.5, 1.0, f));
    lvl = 0.15 + 0.8 * clamp(lvl * (0.7 + 0.6 * h21(vec2(k, floor(uBeat * 4.0)))), 0.0, 1.0);
    float on = step(uv.y, lvl) * step(0.06, fract(uv.x * 12.0)) * step(fract(uv.x * 12.0), 0.94);
    vec3 col = mix(uPalB, uPalA, uv.y / max(lvl, 0.01));
    return col * on * 0.95 + vec3(0.03);
  }
  vec3 channel(float id, vec2 uv, vec2 tileRes) {
    float k = mod(id, 6.0);
    float v = floor(id / 6.0) + 1.0;
    if (k < 0.5) {
      float b = floor(uv.x * 7.0);
      vec3 c = b < 0.5 ? vec3(0.75) : (b < 1.5 ? vec3(0.75, 0.75, 0.0) : (b < 2.5 ? vec3(0.0, 0.75, 0.75) : (b < 3.5 ? vec3(0.0, 0.75, 0.0) : (b < 4.5 ? vec3(0.75, 0.0, 0.75) : (b < 5.5 ? vec3(0.75, 0.0, 0.0) : vec3(0.0, 0.0, 0.75))))));
      if (uv.y < 0.25) c = vec3(0.1 + 0.6 * floor(uv.x * 5.0) / 4.0);
      return c;
    }
    if (k < 1.5) return coverFit(uv, tileRes.x / tileRes.y, 2.6, vec2(0.25 * sin(uBeat * 0.17 * v), 0.2 * cos(uBeat * 0.13 * v)));
    if (k < 2.5) {
      float y = 0.5 + 0.28 * sin(uv.x * 18.0 * v + uTime * 6.0) * (0.3 + 0.7 * uBands.x) + 0.1 * sin(uv.x * 61.0 - uTime * 11.0) * uBands.z;
      float d = abs(uv.y - y) * tileRes.y;
      float grid = step(0.97, fract(uv.x * 8.0)) + step(0.97, fract(uv.y * 6.0));
      return vec3(0.0, 0.08, 0.02) + vec3(0.02, 0.1, 0.03) * grid + vec3(0.3, 1.0, 0.4) * (exp(-d / 4.0) * 0.4 + (1.0 - smoothstep(0.0, 1.8, d)) * 0.8);
    }
    if (k < 3.5) {
      vec2 p = (uv - 0.5) * vec2(tileRes.x / tileRes.y, 1.0);
      float r = length(p), a = atan(p.y, p.x);
      float sweep = step(mod(a / 6.2832 + 0.5 - fract(uBeat * 0.25), 1.0), 0.5);
      vec3 c = vec3(0.55, 0.52, 0.45) * (0.75 + 0.25 * sweep);
      c *= 1.0 - 0.9 * (1.0 - smoothstep(0.0, 2.0, abs(r - 0.42) * tileRes.y));
      c *= 1.0 - 0.9 * (1.0 - smoothstep(0.0, 2.0, min(abs(p.x), abs(p.y)) * tileRes.y)) * step(r, 0.46);
      c *= 1.0 - 0.6 * smoothstep(0.0, 0.04, r - 0.42) * step(0.42, r);
      return c;
    }
    if (k < 4.5) {
      vec2 q = uv * vec2(6.0, 4.0) + vec2(uBeat * 0.5 * v, uBeat * 0.25);
      float chk = mod(floor(q.x) + floor(q.y), 2.0);
      return mix(uPalA, uPalC, chk) * 0.8;
    }
    vec2 p = (uv - 0.5) * vec2(tileRes.x / tileRes.y, 1.0);
    float r = length(p);
    float g = step(0.94, fract(uv.x * 10.0)) + step(0.94, fract(uv.y * 7.0));
    vec3 c = vec3(0.12) + vec3(0.5) * g;
    c = mix(c, vec3(0.9), 1.0 - smoothstep(0.0, 2.0, abs(r - 0.4) * tileRes.y));
    float pk = floor((uv.x - 0.2) / 0.1);
    if (uv.y > 0.6 && uv.y < 0.75 && uv.x > 0.2 && uv.x < 0.8) c = pk < 1.0 ? uPalA : (pk < 2.0 ? uPalB : (pk < 3.0 ? uPalC : (pk < 4.0 ? vec3(0.8) : vec3(0.0))));
    return c;
  }
  void main() {
    vec3 c;
    if (uAtlas < 0.5) c = giant(vUv);
    else {
      vec2 t = floor(vUv * vec2(${ATLAS[0]}.0, ${ATLAS[1]}.0));
      vec2 luv = fract(vUv * vec2(${ATLAS[0]}.0, ${ATLAS[1]}.0));
      c = channel(t.y * ${ATLAS[0]}.0 + t.x, luv, uRes / vec2(${ATLAS[0]}.0, ${ATLAS[1]}.0));
    }
    gl_FragColor = vec4(c, 1.0);
  }
`;

// ce que les deux murs partagent : l'état d'un écran et son contenu
const SCREEN_GLSL = `
  uniform sampler2D uSrc;
  uniform sampler2D uAtlas;
  uniform sampler2D uState;      // 8 x 6 : mode, chaîne, éclair, allumage
  uniform float uTime;
  uniform float uMask;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uRes;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  vec4 stateOf(float idx) { return texture2D(uState, vec2(mod(idx, ${MAXC}.0) + 0.5, floor(idx / ${MAXC}.0) + 0.5) / vec2(${MAXC}.0, ${MAXR}.0)); }
  vec3 atlasAt(float chan, vec2 suv) {
    vec2 t = vec2(mod(chan, ${ATLAS[0]}.0), floor(chan / ${ATLAS[0]}.0));
    return texture2D(uAtlas, (t + clamp(suv, 0.0, 1.0)) / vec2(${ATLAS[0]}.0, ${ATLAS[1]}.0)).rgb;
  }
  // la neige, fine : des grains de 1 à 2 px de l'écran RÉEL
  vec3 snow(float seed) {
    float n = h21(floor(gl_FragCoord.xy / 1.5) + fract(uTime * 37.0 + seed) * 17.0);
    return vec3(0.22 + 0.62 * n * n);
  }
  // le contenu d'un écran : suv dans l'écran, guv la position du pixel dans la source géante
  vec3 content(vec4 st, vec2 suv, vec2 guv, float heroFull, float hc) {
    float mode = floor(st.r * 5.0), chan = floor(st.g * 16.0 + 0.5), flash = st.b;
    vec3 pic;
    if (mode < 0.5) pic = atlasAt(chan, suv);
    else if (mode < 1.5) pic = heroFull > 0.5 ? texture2D(uSrc, suv).rgb : texture2D(uSrc, guv).rgb;
    else if (mode < 2.5) pic = snow(hc);
    else {
      float roll = fract(suv.y + uTime * 1.7 + hc);
      pic = atlasAt(chan, vec2(suv.x, roll));
      pic *= smoothstep(0.0, 0.06, roll) * smoothstep(0.0, 0.03, 1.0 - roll);
    }
    return mix(pic, snow(hc + 3.0), flash);
  }
  // le filtre CRT et l'allumage, puis les reflets du verre en mode screen
  vec3 crt(vec3 pic, vec2 suv, float power, float refl) {
    vec2 q = suv * 2.0 - 1.0;
    float vis = step(abs(q.y), power) * step(abs(q.x), min(1.0, power * 6.0));
    pic *= vis * (0.6 + 0.4 * power);
    pic += vec3(1.0, 0.98, 0.95) * pow(1.0 - power, 3.0) * exp(-length(q) * 24.0) * step(0.001, power);
    pic *= 0.86 + 0.14 * step(1.0, mod(gl_FragCoord.y, 3.0));
    if (uMask > 0.5) {
      float m = mod(gl_FragCoord.x, 3.0);
      pic *= m < 1.0 ? vec3(1.07, 0.965, 0.965) : (m < 2.0 ? vec3(0.965, 1.07, 0.965) : vec3(0.965, 0.965, 1.07));
    }
    pic += pic * 0.18 * smoothstep(0.55, 1.0, dot(pic, vec3(0.2126, 0.7152, 0.0722)));   // le phosphore gonfle sur les blancs
    pic *= 1.0 - 0.32 * pow(dot(q, q), 1.4);
    vec3 r = vec3(refl);
    return 1.0 - (1.0 - pic) * (1.0 - r);                        // screen
  }
  vec3 finish(vec3 col, vec2 uv) {
    col *= uGain;
    vec2 sc = vec2((uv.x * 2.0 - 1.0) * uAspect, uv.y * 2.0 - 1.0);
    vec2 qs = abs(sc - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    return col * mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));
  }
`;

// --- le mur photographié ----------------------------------------------------------
const WALL_PHOTO_FRAGMENT = `
  precision highp float;
  ${SCREEN_GLSL}
  uniform sampler2D uOff;
  uniform sampler2D uIds;        // R id, G et B les deux écrans du spill
  uniform sampler2D uSoft;       // R masque adouci, G reflets, B spill
  uniform sampler2D uUv;         // R, G : (u, v) dans l'écran ; B la part du premier écran du spill
  uniform sampler2D uCentres;    // 8 x 6 : centre de chaque écran (uv photo), héros
  uniform float uHeroFull;
  uniform float uDebug;
  varying vec2 vUv;
  const float PA = ${PHOTO_A.toFixed(5)};
  // la couleur moyenne du contenu d'un écran, pour la lumière qu'il jette
  vec3 glowOf(float idx) {
    vec4 st = stateOf(idx);
    vec4 ce = texture2D(uCentres, vec2(mod(idx, ${MAXC}.0) + 0.5, floor(idx / ${MAXC}.0) + 0.5) / vec2(${MAXC}.0, ${MAXR}.0));
    float mode = floor(st.r * 5.0), chan = floor(st.g * 16.0 + 0.5);
    vec3 c = vec3(0.0);
    for (int i = 0; i < 4; i++) {
      vec2 o = vec2(float(i) - 1.5, float(i) * 0.7 - 1.0) * 0.18;
      if (mode < 0.5 || mode > 2.5) c += atlasAt(chan, vec2(0.5) + o);
      else if (mode < 1.5) c += (ce.b > 0.5 && uHeroFull > 0.5) ? texture2D(uSrc, vec2(0.5) + o).rgb : texture2D(uSrc, ce.rg + o * 0.12).rgb;
      else c += vec3(0.45);
    }
    return c * 0.25 * st.a;
  }
  void main() {
    // la photo COUVRE l'écran (R18) ; toutes les couches sous la même transformation
    vec2 s = vUv - 0.5;
    if (uAspect > PA) s.y *= PA / uAspect; else s.x *= uAspect / PA;
    vec2 puv = s + 0.5;
    vec3 off = texture2D(uOff, puv).rgb;
    vec4 idc = texture2D(uIds, puv);
    vec4 sf = texture2D(uSoft, puv);
    float id = floor(idc.r * 255.0 + 0.5), sp1 = floor(idc.g * 255.0 + 0.5), sp2 = floor(idc.b * 255.0 + 0.5);
    float mask = sf.r, refl = sf.g, spill = sf.b;
    vec3 uvs = texture2D(uUv, puv).rgb;
    vec3 col = off;
    // le spill : la lumière des deux écrans voisins colore le boîtier (pondérés, sans frontière)
    if (spill > 0.002 && sp1 > 0.5) {
      vec3 g = glowOf(sp1 - 1.0) * uvs.b;
      if (sp2 > 0.5) g += glowOf(sp2 - 1.0) * (1.0 - uvs.b);
      col += g * spill * 0.5;
    }
    if (id > 0.5 && mask > 0.002) {
      float idx = id - 1.0;
      vec4 st = stateOf(idx);
      vec4 ce = texture2D(uCentres, vec2(mod(idx, ${MAXC}.0) + 0.5, floor(idx / ${MAXC}.0) + 0.5) / vec2(${MAXC}.0, ${MAXR}.0));
      // v cuit au build depuis le HAUT de l'image (repère image) ; les cibles GL comptent depuis le bas
      vec2 suv = vec2(uvs.r, 1.0 - uvs.g);
      // une légère aberration chromatique au bord du verre
      vec2 q = suv * 2.0 - 1.0;
      vec2 ab = q * dot(q, q) * 0.006;
      float hero = ce.b > 0.5 ? uHeroFull : 0.0;
      float hc = h21(vec2(idx, 1.3));
      vec3 pic = content(st, suv, puv, hero, hc);
      pic.r = content(st, suv + ab, puv + ab * 0.05, hero, hc).r;
      pic.b = content(st, suv - ab, puv - ab * 0.05, hero, hc).b;
      vec3 screen = crt(pic, suv, st.a, refl * 0.9);
      col = mix(col, screen, mask);
      if (uDebug > 0.5) {
        vec3 tint = 0.5 + 0.5 * cos(6.2832 * (idx / 7.0 + vec3(0.0, 0.33, 0.67)));
        col = mix(col, tint, 0.35 * mask);
      }
    }
    if (uDebug > 0.5) {
      float edge = mask * (1.0 - mask) * 4.0;
      col = mix(col, vec3(0.1, 1.0, 0.2), smoothstep(0.3, 0.9, edge));
    }
    gl_FragColor = vec4(finish(col, vUv), 1.0);
  }
`;

// --- le repli : le mur procédural de la première version -------------------------
const WALL_PROC_FRAGMENT = `
  precision highp float;
  ${SCREEN_GLSL}
  uniform vec2 uGrid;
  uniform float uSeed;
  uniform vec2 uPar;
  varying vec2 vUv;
  float sdBox(vec2 p, vec2 b, float r) { vec2 q = abs(p) - b + r; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r; }
  void main() {
    vec2 uv = gl_FragCoord.xy / uRes;
    vec2 cell = floor(uv * uGrid);
    vec2 local = fract(uv * uGrid);
    vec2 cellPx = uRes / uGrid;
    float hc = h21(cell + uSeed), hc2 = h21(cell * 1.7 + uSeed + 3.0), hc3 = h21(cell * 2.3 + uSeed + 9.0);
    float ang = (hc - 0.5) * 0.052;
    vec2 p = (local - 0.5) * cellPx;
    p = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * p;
    vec2 boxHalf = cellPx * 0.5 - 2.0;
    float dBox = sdBox(p, boxHalf, min(cellPx.x, cellPx.y) * 0.07);
    vec3 body = hc2 < 0.4 ? vec3(0.030, 0.030, 0.034) : (hc2 < 0.75 ? vec3(0.085, 0.086, 0.09) : vec3(0.10, 0.058, 0.028));
    body *= 0.85 + 0.3 * h21(floor(p / 3.0) + cell);
    float bevel = smoothstep(0.0, 9.0, -dBox);
    body *= 0.35 + 0.65 * bevel;
    body += vec3(0.08) * (1.0 - smoothstep(0.0, 2.5, abs(p.y - (boxHalf.y - 6.0)))) * step(abs(p.x), boxHalf.x - 14.0);
    float thick = 0.10 + 0.05 * hc3;
    vec2 scrHalf = boxHalf * vec2(1.0 - 2.0 * thick, 1.0 - 2.4 * thick);
    vec2 scrC = vec2(0.0, boxHalf.y * thick * 0.9) + uPar;
    vec2 q = (p - scrC) / scrHalf;
    float dScr = sdBox(p - scrC, scrHalf, min(scrHalf.x, scrHalf.y) * 0.22);
    vec3 col = body * step(dBox, 0.0);
    vec2 kb = p - vec2(boxHalf.x * 0.62, -boxHalf.y * (1.0 - thick * 1.1));
    float knob = min(length(kb - vec2(-10.0, 0.0)), length(kb + vec2(-4.0, 0.0))) - 3.2;
    col = mix(col, vec3(0.22) * (0.6 + 0.6 * smoothstep(0.0, 3.0, -(knob))), 1.0 - smoothstep(0.0, 1.2, knob));
    float led = length(p - vec2(-boxHalf.x * 0.7, -boxHalf.y * (1.0 - thick * 1.1))) - 1.6;
    float idx = cell.y * ${MAXC}.0 + cell.x;
    vec4 st = stateOf(idx);
    col = mix(col, vec3(0.9, 0.08, 0.04) * (0.3 + 0.7 * st.a), 1.0 - smoothstep(0.0, 1.2, led));
    if (dScr < 0.0) {
      vec2 qb = q * (1.0 + 0.13 * dot(q, q));
      vec2 suv = qb * 0.5 + 0.5;
      float inside = step(max(abs(qb.x), abs(qb.y)), 1.0);
      vec3 pic = vec3(0.0);
      if (inside > 0.5) {
        vec2 guv = (cell + suv) / uGrid;
        pic = content(st, suv, guv, 0.0, hc);
        pic = crt(pic, suv, st.a, 0.0);
      }
      vec2 sp = (q - vec2(-0.45, 0.55)) * vec2(1.4, 2.6);
      col = pic + vec3(exp(-dot(sp, sp) * 1.6) * 0.22) + vec3(0.012) * inside;
    }
    gl_FragColor = vec4(finish(col, uv), 1.0);
  }
`;

export class TvwallVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const g = /^(\d+)x(\d+)$/.exec(urlParam('tvgrid') || '');
    this._forceGrid = g ? [Math.min(MAXC, Math.max(2, +g[1])), Math.min(MAXR, Math.max(2, +g[2]))] : null;
    this._forceSync = urlParam('tvsync') === '1';
    this._forceSnow = urlParam('tvsnow') === '1';
    this._forceHero = urlParam('tvhero') === '1';
    this._debugScreens = urlParam('tvscreens') === '1';
    this._post = new VdjPost({ bloomStrength: 0.3, bloomThreshold: 0.78, grain: 0.02, vignette: 0.3, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const A = getTvwall();
    this._photo = !!A;
    this._A = A;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    this._palA = new THREE.Color(0.9, 0.2, 0.5); this._palB = new THREE.Color(0.2, 0.7, 0.95); this._palC = new THREE.Color(1.0, 0.75, 0.1);
    this._titleTex = titleTexture('HALO') || new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    this._titleTex.needsUpdate = true;
    this._title = ''; this._coverUrl = undefined; this._coverTex = null;
    // l'état des écrans (48 au plus) et leurs centres
    const n = MAXC * MAXR;
    this._stateData = new Uint8Array(n * 4);
    this._stateTex = new THREE.DataTexture(this._stateData, MAXC, MAXR, THREE.RGBAFormat);
    this._stateTex.minFilter = this._stateTex.magFilter = THREE.NearestFilter; this._stateTex.generateMipmaps = false; this._stateTex.needsUpdate = true;
    const cd = new Uint8Array(n * 4);
    this._mode = new Uint8Array(n); this._chan = new Uint8Array(n); this._flash = new Float32Array(n); this._power = new Float32Array(n).fill(1);
    this._giantUntil = new Float32Array(n); this._giantAt = new Float32Array(n); this._rollUntil = new Float32Array(n); this._snowUntil = new Float32Array(n);
    this._offAt = new Float32Array(n).fill(-9);
    for (let i = 0; i < n; i++) this._chan[i] = Math.floor(hash1(i * 3.7 + 1) * 12);
    if (this._photo) {
      // les écrans de la photo : l'indice i = id - 1 ; centres en uv photo (y vers le bas dans l'image, vers le haut dans la texture)
      this._screens = A.screens.slice(0, n).map((s) => ({ idx: s.id - 1, cx: s.cx, cy: s.cy, hero: !!s.hero, area: s.area }));
      for (const s of this._screens) { const o = s.idx * 4; cd[o] = Math.round(s.cx * 255); cd[o + 1] = Math.round((1 - s.cy) * 255); cd[o + 2] = s.hero ? 255 : 0; cd[o + 3] = 255; }
    }
    this._centresTex = new THREE.DataTexture(cd, MAXC, MAXR, THREE.RGBAFormat);
    this._centresTex.minFilter = this._centresTex.magFilter = THREE.NearestFilter; this._centresTex.generateMipmaps = false; this._centresTex.needsUpdate = true;

    const L = this._layout;
    const mk = (fs, uniforms) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms, vertexShader: QUAD_VERTEX, fragmentShader: fs, depthTest: false, depthWrite: false }));
      m.frustumCulled = false;
      const sc = new THREE.Scene(); sc.add(m);
      return { scene: sc, mesh: m, u: uniforms };
    };
    this._content = mk(CONTENT_FRAGMENT, {
      uCover: { value: null }, uTitle: { value: this._titleTex }, uHasCover: { value: 0 }, uCoverAspect: { value: 1 },
      uAtlas: { value: 0 }, uGiant: { value: 0 }, uTime: { value: 0 }, uBeat: { value: 0 }, uBands: { value: new THREE.Vector3(0.3, 0.3, 0.3) },
      uPalA: { value: this._palA }, uPalB: { value: this._palB }, uPalC: { value: this._palC }, uRes: { value: new THREE.Vector2(L.srcW, L.srcH) }
    });
    const shared = {
      uSrc: { value: null }, uAtlas: { value: null }, uState: { value: this._stateTex }, uTime: { value: 0 },
      uMask: { value: L.mask }, uGain: { value: 1 }, uRes: { value: new THREE.Vector2(1920, 1080) },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    if (this._photo) {
      this._u = Object.assign(shared, {
        uOff: { value: A.off }, uIds: { value: A.ids }, uSoft: { value: A.soft }, uUv: { value: A.uv }, uCentres: { value: this._centresTex },
        uHeroFull: { value: 0 }, uDebug: { value: this._debugScreens ? 1 : 0 }
      });
    } else {
      this._u = Object.assign(shared, { uGrid: { value: new THREE.Vector2(4, 3) }, uSeed: { value: 0 }, uPar: { value: new THREE.Vector2() } });
    }
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: QUAD_VERTEX, fragmentShader: this._photo ? WALL_PHOTO_FRAGMENT : WALL_PROC_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    this._measScene = new THREE.Scene();
    const mq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._quad.material);
    mq.frustumCulled = false;
    this._measScene.add(mq);
    this._measRT = new THREE.WebGLRenderTarget(32, 32, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._measBuf = new Uint8Array(32 * 32 * 4);

    this._time = 0; this._beats = 0; this._bars = 0; this._phraseN = 0; this._beatAge = 9; this._barAge = 9;
    this._gridIdx = 0; this._giant = 0; this._palette = 0; this._paletteRef = undefined; this._build = 0; this._dropAge = 99;
    this._beatS = 0.5; this._quarter = 0.125; this._heroFull = 0;
    this._lumAge = 0; this._lumMeasured = 0.3; this._lumFresh = false; this._gain = 1; this._paused = false; this._dirty = false;
    this._applyPalette(null);
    this._setGrid();
    this._startSync(0, 0, 2);                             // la scène : la pochette dans les 2 premières secondes
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff3388, 0x33bbff, 0xffbb22];
    const sh = HUE_SHIFTS[this._palette % HUE_SHIFTS.length];
    const a = vdjColor(v[0], new THREE.Color(), VDJ_PRESETS.party.saturation); a.offsetHSL(sh, 0, 0);
    const b = vdjColor(v[1 % v.length], new THREE.Color(), VDJ_PRESETS.party.saturation); b.offsetHSL(sh + 0.4, 0, 0);
    const c = vdjColor(v[2 % v.length], new THREE.Color(), VDJ_PRESETS.party.saturation); c.offsetHSL(sh + 0.7, 0, 0);
    this._palA.copy(a); this._palB.copy(b); this._palC.copy(c);
  }

  // les écrans actifs : ceux de la photo, ou les cellules de la grille du repli
  _setGrid() {
    if (this._photo) return;
    const grids = this._layout.grids;
    const g = this._forceGrid || grids[this._gridIdx % grids.length];
    this._cols = g[0]; this._rows = g[1];
    this._u.uGrid.value.set(g[0], g[1]);
    this._u.uSeed.value = this._gridIdx * 17.3;
    this._screens = [];
    for (let r = 0; r < this._rows; r++) for (let c = 0; c < this._cols; c++) this._screens.push({ idx: r * MAXC + c, cx: (c + 0.5) / this._cols, cy: (r + 0.5) / this._rows, hero: false });
  }

  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._content.u.uHasCover.value = 0;
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.MirroredRepeatWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._content.u.uCover.value = tex; self._content.u.uHasCover.value = 1;
      const img = tex.image;
      self._content.u.uCoverAspect.value = img && img.width ? img.width / img.height : 1;
    }, () => {});
  }

  onTrackChange(profile) {
    this._loadCover(profile);
    const t = titleCaps(profile && profile.title);
    if (t !== this._title) {
      this._title = t;
      const tex = titleTexture(t || 'HALO');
      if (tex) { if (this._titleTex) this._titleTex.dispose(); this._titleTex = tex; this._content.u.uTitle.value = tex; }
    }
  }

  // la synchronisation : en vague depuis le centre (0) ou en diagonale (1), sur 1/4 de temps
  _startSync(now, wave, holdBeats) {
    let maxD = 1e-3;
    for (const s of this._screens) maxD = Math.max(maxD, Math.hypot(s.cx - 0.5, s.cy - 0.5));
    for (const s of this._screens) {
      const d = wave === 0 ? Math.hypot(s.cx - 0.5, s.cy - 0.5) / maxD : (s.cx + s.cy) / 2;
      this._giantAt[s.idx] = now + d * this._quarter;
      this._giantUntil[s.idx] = this._giantAt[s.idx] + holdBeats * this._beatS;
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.artworkUrl !== this._coverUrl) this._loadCover(profile);
    if (this._title === '' && profile.title) this.onTrackChange(profile);
    if (paused) return;                                  // l'image tient
    this._time += dt;
    this._clock.update(dt);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 124;
    const beatS = this._beatS = 60 / bpm, barS = 4 * beatS;
    this._quarter = beatS / 4;
    const now = this._time;
    const director = profile.director || null;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 1.0));
    const calm = a.intensity < 0.6;
    const S = this._screens, n = S.length;
    const rnd = (k) => hash1(this._beats * 7.1 + k * 13.7 + 0.5);
    const low = a.bands ? a.bands.low : a.pulse;
    const snowCap = Math.min(4, Math.floor(n * SNOW_MAX_FRACTION));

    if (a.beat) {
      this._beats++; this._beatAge = 0;
      const bib = ((this._beats % 4) + 4) % 4;
      // 2 à 4 écrans changent de chaîne (1 sous 0,6), avec un éclair de neige
      const nSwitch = calm ? 1 : 2 + Math.floor(rnd(1) * 3);
      for (let k = 0; k < nSwitch; k++) {
        const s = S[Math.floor(rnd(2 + k) * n)];
        this._chan[s.idx] = (this._chan[s.idx] + 1 + Math.floor(rnd(9 + k) * 11)) % 12;
        this._flash[s.idx] = 1;
      }
      // le décrochage sur les temps 2 et 4
      if (!calm && (bib === 1 || bib === 3)) for (let k = 0; k < 1 + Math.floor(rnd(20) * 2); k++) this._rollUntil[S[Math.floor(rnd(21 + k) * n)].idx] = now + 0.6 * beatS;
      // la neige « en panne » : sous le plafond de 20 % (4 écrans au plus), plus souvent en montée
      let snowing = 0;
      for (const s of S) if (now < this._snowUntil[s.idx]) snowing++;
      const want = (calm ? 0.08 : 0.25) + 0.6 * this._build;
      for (let k = 0; k < 2 && snowing < snowCap; k++) if (rnd(40 + k) < want) { this._snowUntil[S[Math.floor(rnd(60 + k) * n)].idx] = now + beatS * (0.5 + rnd(80 + k)); snowing++; }
      if (this._build > 0.5 && bib !== 0) this._startSync(now, Math.floor(rnd(30) * 2), 0.5);
      // le kick : un écran s'éteint et se rallume
      if (low > 0.55 && !calm) this._offAt[S[Math.floor(rnd(50) * n)].idx] = now;
    }
    this._beatAge += dt;
    if (a.bar) {
      this._bars++; this._barAge = 0;
      this._startSync(now, Math.floor(rnd(31) * 2), 1);   // la synchronisation générale, un temps
      this._heroFull = this._bars % 2;                     // le héros : la source entière une mesure sur deux
    }
    this._barAge += dt;
    if (a.phrase) {
      this._phraseN++; this._gridIdx++; this._giant = (this._giant + 1) % GIANTS; this._palette++;
      this._applyPalette(profile.artworkPalette); this._setGrid();
    }
    if (this._clock.wrapped) { this._giant = 0; this._startSync(now, 0, 2); }
    if (a.drop) {
      this._dropAge = 0; this._giant = 0;
      for (const s of S) this._offAt[s.idx] = now - 0.05;
      this._dropSync = now + beatS;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    this._dropAge += dt;
    if (this._dropSync != null && now >= this._dropSync) { this._dropSync = null; this._startSync(now, 0, 2); for (const s of S) { this._offAt[s.idx] = -9; this._flash[s.idx] = 1; } }

    // l'état de chaque écran
    const bp = a.beatPhase != null ? a.beatPhase : clamp01(this._beatAge / beatS);
    for (const s of S) {
      const i = s.idx;
      let mode = MODE.channel;
      if (now >= this._giantAt[i] && now < this._giantUntil[i]) {
        mode = MODE.giant;
        if (now - this._giantAt[i] < dt * 1.5 || this._giantUntil[i] - now < dt * 1.5) this._flash[i] = Math.max(this._flash[i], 0.9);
      }
      if (now < this._rollUntil[i]) mode = MODE.roll;
      if (now < this._snowUntil[i]) mode = MODE.snow;
      if (this._forceSync) mode = MODE.giant;
      if (this._forceSnow) mode = MODE.snow;
      this._mode[i] = mode;
      // l'éclair de neige d'une bascule : 1/8 à 1/4 de temps
      this._flash[i] *= Math.exp(-dt / (beatS * 0.06));
      const off = now - this._offAt[i];
      let power = 1;
      if (off >= 0) {
        const hold = this._dropAge < 2 ? beatS : 0.12;
        if (off < 0.18) power = 1 - off / 0.18;
        else if (off < 0.18 + hold) power = 0;
        else power = clamp01((off - 0.18 - hold) / 0.25);
      }
      this._power[i] = power;
      const o = i * 4;
      this._stateData[o] = Math.round((mode + 0.5) / 5 * 255);
      this._stateData[o + 1] = Math.round(this._chan[i] / 16 * 255);
      this._stateData[o + 2] = Math.round(clamp01(this._flash[i]) * 255);
      this._stateData[o + 3] = Math.round(power * 255);
    }
    this._stateTex.needsUpdate = true;

    const cu = this._content.u;
    cu.uTime.value = now; cu.uBeat.value = this._beats + bp; cu.uGiant.value = this._giant;
    const b = a.bands || { low: 0.4 + 0.4 * a.pulse, mid: 0.4 * a.intensity, high: 0.3 * a.intensity };
    cu.uBands.value.set(clamp01(b.low), clamp01(b.mid), clamp01(b.high));
    this._u.uTime.value = now;
    if (this._photo) this._u.uHeroFull.value = this._forceHero ? 1 : this._heroFull;
    else this._u.uPar.value.set(2.0 * Math.sin(now * 0.37), 1.5 * Math.sin(now * 0.23 + 1.0));
    this._dirty = true;
    this._lumAge += dt;
    if (this._lumFresh) { this._lumFresh = false; this._gain = this._comfort.measure(this._lumMeasured, 0.5); }
    this._u.uGain.value = this._gain;
    this._post.update(vdjBeatChroma(bp, beatS));
  }

  _ensureTargets(renderer) {
    if (this._rt) return;
    const L = this._layout;
    const mk = () => new THREE.WebGLRenderTarget(L.srcW, L.srcH, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping, generateMipmaps: false });
    this._rt = { src: mk(), atlas: mk() };
  }

  _render(renderer) {
    this._ensureTargets(renderer);
    // la taille de la CIBLE courante (la passe de rendu du composer), pas du
    // canvas : gl_FragCoord vit dans la cible, et au tier 0 elle est plus
    // petite - avec la taille du canvas la planche ne remplissait que le
    // coin bas-gauche
    const cur = renderer.getRenderTarget();
    if (cur) _size.set(cur.width, cur.height); else renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, _size.x), h = Math.max(2, _size.y);
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_cc); const ca = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (this._dirty) {
      this._dirty = false;
      const cu = this._content.u;
      cu.uAtlas.value = 0;
      renderer.setRenderTarget(this._rt.src);
      renderer.render(this._content.scene, this._ortho);
      cu.uAtlas.value = 1;
      renderer.setRenderTarget(this._rt.atlas);
      renderer.render(this._content.scene, this._ortho);
    }
    this._u.uSrc.value = this._rt.src.texture; this._u.uAtlas.value = this._rt.atlas.texture;
    if (this._lumAge >= 0.5 && !this._paused) {
      this._lumAge = 0;
      this._u.uRes.value.set(32, 32);
      renderer.setRenderTarget(this._measRT);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, false, false);
      renderer.render(this._measScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._measRT, 0, 0, 32, 32, this._measBuf);
        let sum = 0;
        for (let i = 0; i < this._measBuf.length; i += 4) sum += 0.2126 * this._measBuf[i] + 0.7152 * this._measBuf[i + 1] + 0.0722 * this._measBuf[i + 2];
        this._lumMeasured = sum / (32 * 32 * 255);
        this._lumFresh = true;
      } catch (e) { /* la prochaine fois */ }
      renderer.setClearColor(_cc, ca);
    }
    this._u.uRes.value.set(w, h);
    renderer.setRenderTarget(prev);
    renderer.autoClear = autoClear;
  }

  getProbe() {
    let giant = 0, snow = 0, roll = 0, off = 0;
    for (const s of this._screens) { const m = this._mode[s.idx]; if (m === 1) giant++; else if (m === 2) snow++; else if (m === 3) roll++; if (this._power[s.idx] < 0.5) off++; }
    return { t: +this._time.toFixed(2), photo: this._photo, screens: this._screens.length, beats: this._beats, bars: this._bars, giantType: this._giant, heroFull: this._heroFull, giant, snow, roll, off, build: +this._build.toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), cover: this._content.u.uHasCover.value, title: this._title };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    if (this._content) { this._content.mesh.geometry.dispose(); this._content.mesh.material.dispose(); }
    if (this._rt) { this._rt.src.dispose(); this._rt.atlas.dispose(); }
    if (this._measRT) this._measRT.dispose();
    if (this._stateTex) this._stateTex.dispose();
    if (this._centresTex) this._centresTex.dispose();
    if (this._titleTex) this._titleTex.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
